import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { goBack } from '../../utils/navigation';

export default function VerificarInputPage() {
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!codigo.trim()) {
      setError('Ingresá el código de verificación');
      return;
    }
    
    // Navegar a la página de verificación con el código
    navigate(`/verificar/${codigo.trim()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
            <button
              onClick={() => goBack()}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-lg mx-auto px-4 py-12">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-10 h-10 text-emerald-600" />
              </div>
              Verificar Autenticidad
            </CardTitle>
            <p className="text-center text-gray-600 text-sm">
              Ingresá el código de verificación que aparece en el certificado o constancia para comprobar su autenticidad.
            </p>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700 text-sm text-center">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="codigo">Código de verificación</Label>
                <Input
                  id="codigo"
                  value={codigo}
                  onChange={(e) => {
                    setCodigo(e.target.value);
                    setError(null);
                  }}
                  placeholder="Ingresá el código del QR o documento"
                  className="text-center text-lg tracking-wider"
                  data-testid="input-codigo-verificacion"
                />
                <p className="text-xs text-gray-500 mt-2 text-center">
                  El código se encuentra debajo del código QR del documento
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-verificar"
              >
                <Search className="w-4 h-4 mr-2" />
                Verificar Documento
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t">
              <h3 className="font-medium text-gray-900 mb-3 text-center">¿Cómo funciona?</h3>
              <ol className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 text-emerald-700 font-medium text-xs">1</span>
                  <span>Escaneá el código QR del certificado o ingresá el código manualmente</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 text-emerald-700 font-medium text-xs">2</span>
                  <span>El sistema verificará si el documento fue emitido oficialmente</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 text-emerald-700 font-medium text-xs">3</span>
                  <span>Verás los datos del titular y del curso para confirmar la autenticidad</span>
                </li>
              </ol>
            </div>

            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-500 text-center">
                Este sistema permite verificar la autenticidad de certificados y constancias
                emitidos por el Colegio Profesional de Trabajadorxs Sociales de Lomas de Zamora.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Link to emit documents */}
        <div className="text-center mt-6">
          <p className="text-gray-600 text-sm mb-2">¿Necesitás tu certificado?</p>
          <Link
            to="/capacitaciones/emitir"
            className="text-emerald-600 hover:underline text-sm font-medium"
          >
            Emitir constancia o certificado →
          </Link>
        </div>
      </main>
    </div>
  );
}
